<template>
  <div
    ref="doc_scan_viewer"
    class="document_scan"
    :class="{ '_show-thumbnails': showThumbnails }" 
  >
    <loader-dots v-if="isLoading.scanPages" />

    <div v-else-if="loadErrors.scanPages.status" class="message _error">
      {{ loadErrors.scanPages.message }}
    </div>

    <document-scan-page
      v-for="(page, index) in pages"
      :key="index"
      :ref="'scanPages'"
      :page="page"
      :visible="visiblePages[index]"
      :show-thumbnail="showThumbnails"
      :data-index="index"
      :marks="marks[index + 1]"
      :current-mark="currentMark"
      :card="card"
    />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import { mapGetters } from 'vuex';

import DocumentScanPage from './DocumentScanPage';
import LoaderDots from '@@/shared/loaders/LoaderDots';

import NormaCsLinkHandler from '@@/document/mixins/NormaCsLinkHandler';

export default {
  components: {
    DocumentScanPage,
    LoaderDots
  },

  mixins: [NormaCsLinkHandler],

  props: {
    pages: {
      type: Array,
      default: () => []
    },
    /** @type {Object|Promise<Array<ApiDocumentScanPage>>} */
    documentId: {
      type: String,
      required: true
    },
    fragmentId: {
      type: String,
      default: () => null
    },
    showThumbnails: {
      type: Boolean,
      default: () => false
    },
    viewMode: {
      type: String,
      default: () => 'scan'
    },
    scrollTop: {
      type: Object,
      default: () => ({
        index: 0,
        shift: 0
      })
    }
  },

  data: () => ({
    visiblePages: [],
    observer: null,
    currentDocumentId: null,
    currentFragmentId: null
  }),

  computed: {
    ...mapGetters({
      isLoading: 'documentIsLoading',
      loadErrors: 'loadErrors',
      allMarks: 'foundScanMatches',   // Не объект, а модифицированный массив
      currentMarkIndex: 'currentSearchMatch',  // Индекс текущей метки
      card: 'currentDocumentInfo'
    }),
    marks() {
      let obj = {}

      // Split linear all marks array in object by pages
      this.allMarks.forEach( item => {
        if(!(item.page in obj)) obj[item.page] = []
        obj[item.page].push(item) 
      })

      return obj
    },
    currentMark() {
      return (this.allMarks.length && this.currentMarkIndex)? 
        this.allMarks[this.currentMarkIndex-1]: null;
    }
  },

  watch: {
    fragmentId(newFragmentId) {
      if (this.currentFragmentId !== newFragmentId) {
        this.currentFragmentId = newFragmentId;
        if (newFragmentId) {
          this.scrollToAnchor(newFragmentId);
        } else {
          this.scrollTo(0);
        }
      }
    },
    currentMark(mark) {
      if (mark) {
        this.$nextTick(() => {
          this.scrollToCurrentMark();
        });
      }
    },
    scrollTop(newScroll) {
      if(this.viewMode === 'views') {
        let el = this.$refs.doc_scan_viewer.querySelector(`.document-scan-page[data-index="${newScroll.index}"]`)

        if( el ) {      
          this.$refs.doc_scan_viewer.scrollTo(0, el.offsetTop+newScroll.shift*el.offsetHeight)
        }
      }
    }
  },

  created() {
    // текущий адрес страницы
    let href = window.location.href;
    let i = href.indexOf("scan##");
    if (i != -1)
      this.currentFragmentId = href.substr(i + 6) - 1;
    //this.currentFragmentId = this.fragmentId;
  },

  mounted() {    
    let targets = [];
    const processIntersectedTargets = debounce(() => {
      targets.slice(this.showThumbnails ? 0 : -3).forEach(target => {
        this.visiblePages.splice(target.getAttribute('data-index'), 1, true);
        this.observer.unobserve(target);
      });
      targets = [];
    }, 100);

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach((entry, ind) => {
          if (entry.isIntersecting) {
            targets.push(entry.target);
            processIntersectedTargets();
          }
        });
      },
      { root: null, rootMargin: '200px 0px' }
    );
    this.$once('hook:beforeDestroy', () => {
      this.observer.disconnect();
    });
    this.$el.addEventListener('click', e => {
      let page = e.target.closest('.document-scan-page');

      if (page) {
        let pageIndex = page.getAttribute('data-index');
        this.$emit('page-click', pageIndex);
      }
    });
    this.initPages(this.currentFragmentId);
    this.initNormaCsLinkHandler(this.$el, () => this.currentDocumentId);
    this.$refs.doc_scan_viewer.addEventListener('scroll', this.handleWrapperScroll)
  },

  beforeDestroy() {
    this.$refs.doc_scan_viewer.removeEventListener('scroll', this.handleWrapperScroll)
  },

  methods: {
    handleWrapperScroll(ev){
      let rect = this.$refs.doc_scan_viewer.getBoundingClientRect()
      let el = document.elementFromPoint(rect.x+100, rect.y+2)
      let elWrapper = el.closest('.document-scan-page')
      
      if( elWrapper ) {
        let index = +elWrapper.dataset.index
        let shift = (ev.target.scrollTop - elWrapper.offsetTop) / elWrapper.offsetHeight
 
        this.$emit('scan-scroll-update', {
          index:index, 
          shift: shift
        })
      }
    },
    initPages(fragmentId = null) {
      // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      //       TODO: Check observer set only once
      // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
      this.visiblePages = new Array(this.pages.length).fill(false);
      this.currentDocumentId = this.documentId;
      if (this.$refs.scanPages) {
        this.$refs.scanPages.forEach(page => {
          this.observer.observe(page.$el);
        });

        if (fragmentId) {
          this.scrollToAnchor(fragmentId);
        } else {
          this.scrollTo(0);
        }
      }
    },
    //   if (error.response && error.response.status === 403) {
    //     this.loadingError = 'Доступ к документу запрещен.';
    //   } else {
    //     this.loadingError = `Ошибка: ${error.message}`;
    //   }
    scrollTo(y) {
      const scrollable = this.$parent.$refs.documentScroll;

      if (scrollable) scrollable.scrollTop = y;
    },
    scrollToAnchor(anchorId) {
      let pageIndex = anchorId;
      let pages = this.$refs.scanPages;
      let page = pages && pages[pageIndex];

      if (page) {
        this.scrollTo(page.$el.offsetTop);
      }
    },
    scrollToCurrentMark() {
      let mark = this.$el.querySelector('.document-scan-page__mark._current');

      if (mark) {
        let rect = mark.children[0] || mark;

        this.scrollTo(rect.offsetTop + mark.parentNode.offsetTop - 60);
      }
    }
  }
};
</script>
